import React from "react";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import CloudSyncRoundedIcon from "@mui/icons-material/CloudSyncRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import AutoAwesomeMotionRoundedIcon from "@mui/icons-material/AutoAwesomeMotionRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import DnsRoundedIcon from "@mui/icons-material/DnsRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DescriptionIcon from "@mui/icons-material/Description";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";

const HEADERS = ["ID", "ESTADO", "PRIORIDAD", "TITULO", "DESCRIPCION", "NOTA_HUMANA", "RESULTADO_BOT", "INTENTOS", "ORIGEN", "ULTIMA_EJECUCION", "GUIA_REVISION_HUMANA"];

const EMPTY_FORM = {
  enabled: true,
  spreadsheetId: "",
  sheetName: "Backlog",
  serviceAccountEmail: "",
  privateKey: "",
  idPrefix: "MEJ",
  origin: "JARVIS",
  reviewEnabled: true,
};

const TAB_META = [
  { value: "overview", label: "Resumen", icon: <InsightsRoundedIcon fontSize="small" /> },
  { value: "learning", label: "Aprendizaje", icon: <AutoAwesomeMotionRoundedIcon fontSize="small" /> },
  { value: "backlog", label: "Backlog", icon: <DnsRoundedIcon fontSize="small" /> },
  { value: "connection", label: "Conexión", icon: <SettingsRoundedIcon fontSize="small" /> },
];

function dateLabel(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date);
}

function statusTone(status) {
  if (status === "SOLUCIONADO") return "success";
  if (status === "HECHO_POR_BOT") return "warning";
  if (status === "TRABAJANDO") return "info";
  return "default";
}

function backlogItemId(item) {
  return item?.externalId || item?.localRef || `LOCAL-${String(item?._id || "").slice(-6).toUpperCase()}`;
}

function clampPercent(value) {
  return Math.max(0, Math.min(100, Number(value || 0)));
}

function MetricCard({ label, value, detail, tone = "default", icon }) {
  const toneMap = {
    success: { border: "success.main", chip: "success" },
    warning: { border: "warning.main", chip: "warning" },
    info: { border: "info.main", chip: "info" },
    error: { border: "error.main", chip: "error" },
    default: { border: "divider", chip: "default" },
  };
  const current = toneMap[tone] || toneMap.default;
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.6,
        borderRadius: 3,
        borderColor: current.border,
        minHeight: 112,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: .2 }}>{label}</Typography>
        <Chip size="small" color={current.chip} variant="outlined" icon={icon} label={detail || ""} sx={{ maxWidth: 170 }} />
      </Stack>
      <Typography sx={{ fontSize: { xs: 28, md: 34 }, lineHeight: 1, fontWeight: 950, mt: 1 }}>{value}</Typography>
    </Paper>
  );
}

function SectionCard({ title, description, action, children, sx }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 3.5, overflow: "hidden", ...sx }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "flex-start" }} sx={{ p: { xs: 1.4, md: 1.8 } }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950 }}>{title}</Typography>
          {description ? <Typography variant="body2" color="text.secondary" sx={{ mt: .25, maxWidth: 900 }}>{description}</Typography> : null}
        </Box>
        {action || null}
      </Stack>
      <Divider />
      <Box sx={{ p: { xs: 1.4, md: 1.8 } }}>{children}</Box>
    </Paper>
  );
}

function EmptyState({ children }) {
  return (
    <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
      <CheckCircleRoundedIcon color="success" sx={{ fontSize: 34, mb: .7 }} />
      <Typography color="text.secondary">{children}</Typography>
    </Box>
  );
}

export default function OwnerJarvisLabPage() {
  const location = useLocation();
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [config, setConfig] = React.useState(null);
  const [items, setItems] = React.useState([]);
  const [queue, setQueue] = React.useState(null);
  const [reviewEditing, setReviewEditing] = React.useState(false);
  const [reviewNote, setReviewNote] = React.useState("");
  const [resultExpanded, setResultExpanded] = React.useState(false);
  const [gaps, setGaps] = React.useState([]);
  const [capabilities, setCapabilities] = React.useState([]);
  const [coverage, setCoverage] = React.useState(null);
  const [metrics, setMetrics] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [error, setError] = React.useState("");
  const requestedTab = String(location.state?.ownerJarvisUi?.section || "");
  const [tab, setTab] = React.useState(() => TAB_META.some((item) => item.value === requestedTab) ? requestedTab : "overview");

  React.useEffect(() => {
    if (TAB_META.some((item) => item.value === requestedTab)) setTab(requestedTab);
  }, [requestedTab, location.state?.ownerJarvisUi?.token]);

  const patch = (name, value) => setForm((current) => ({ ...current, [name]: value }));

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [configResponse, itemsResponse, queueResponse, gapsResponse, catalogResponse, coverageResponse, metricsResponse] = await Promise.all([
        ownerApi.jarvisBacklogConfig().catch(() => ({ config: null })),
        ownerApi.listJarvisBacklog({ limit: 80 }).catch(() => ({ items: [] })),
        ownerApi.jarvisBacklogQueue({ refresh: true }).catch(() => ({ summary: null })),
        ownerApi.jarvisCapabilityGaps({ limit: 100, status: "open" }).catch(() => ({ gaps: [] })),
        ownerApi.jarvisCapabilityCatalog().catch(() => ({ capabilities: [] })),
        ownerApi.jarvisCoverage().catch(() => ({ coverage: null })),
        ownerApi.jarvisMetrics({ days: 7 }).catch(() => ({ metrics: null })),
      ]);
      const next = configResponse?.config || null;
      setConfig(next);
      setForm({ ...EMPTY_FORM, ...(next || {}), privateKey: "" });
      setItems(itemsResponse?.items || []);
      setQueue(queueResponse?.summary || null);
      setReviewEditing(false);
      setReviewNote("");
      setResultExpanded(false);
      setGaps(gapsResponse?.gaps || []);
      setCapabilities(catalogResponse?.capabilities || []);
      setCoverage(coverageResponse?.coverage || null);
      setMetrics(metricsResponse?.metrics || null);
    } catch (err) {
      setError(formatApiError(err, "No pude leer Jarvis Lab."));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const run = async (key, work, success) => {
    setBusy(key);
    setError("");
    setNotice("");
    try {
      const response = await work();
      setNotice(typeof success === "function" ? success(response) : success);
      await load();
      return response;
    } catch (err) {
      setError(formatApiError(err, "No pude completar la operación."));
      return null;
    } finally {
      setBusy("");
    }
  };

  const save = () => run("save", () => ownerApi.saveJarvisBacklogConfig({
    enabled: form.enabled,
    spreadsheetId: form.spreadsheetId,
    sheetName: form.sheetName,
    serviceAccountEmail: form.serviceAccountEmail,
    ...(form.privateKey.trim() ? { privateKey: form.privateKey } : {}),
    idPrefix: form.idPrefix,
    origin: form.origin,
    reviewEnabled: form.reviewEnabled,
  }), "Conexión guardada. Ahora podés probarla.");

  const test = () => run("test", () => ownerApi.testJarvisBacklog(), (response) => {
    if (response?.needsPrepare) {
      if (response?.tabExists) return `Conexión correcta. La pestaña “${response?.targetTab || form.sheetName || "Backlog"}” existe pero todavía necesita el encabezado.`;
      return `Conexión correcta. Google dio acceso a la planilla; falta crear/preparar la pestaña “${response?.targetTab || form.sheetName || "Backlog"}”.`;
    }
    return "Conexión, pestaña y formato de columnas correctos.";
  });

  const prepare = () => run("prepare", () => ownerApi.prepareJarvisBacklog(), "Encabezado verificado/preparado sin tocar filas existentes.");

  const sync = async () => {
    setBusy("sync");
    setError("");
    setNotice("");
    try {
      const response = await ownerApi.syncJarvisBacklog();
      const result = response?.sync || {};
      await load();
      if (result?.status === "partial" || Number(result?.failed || 0) > 0) {
        const sample = (result?.failures || []).slice(0, 2).map((row) => `${row.id || row.title}: ${row.message}`).join(" · ");
        setError(`Sincronización parcial: ${result.failed || 0} fila(s) no llegaron a Google Sheets.${sample ? ` ${sample}` : ""}`);
        setTab("backlog");
        return response;
      }
      const pieces = [];
      if (Number(result?.mirrored || 0)) pieces.push(`${result.mirrored} leídas de Sheets`);
      if (Number(result?.pushed || 0)) pieces.push(`${result.pushed} enviadas`);
      if (Number(result?.assignedIds || 0)) pieces.push(`${result.assignedIds} IDs asignados`);
      if (Number(result?.restored || 0)) pieces.push(`${result.restored} filas restauradas`);
      setNotice(`Sincronización completa${pieces.length ? ` · ${pieces.join(" · ")}` : ""}.`);
      return response;
    } catch (err) {
      setError(formatApiError(err, "No pude sincronizar Google Sheets."));
      return null;
    } finally {
      setBusy("");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("¿Desconectar Google Sheets? Los fixes locales no se borran.")) return;
    await run("delete", () => ownerApi.deleteJarvisBacklogConfig(), "Google Sheets quedó desconectado. El backlog local se conserva.");
  };

  const currentQueueItem = queue?.current?.item || null;

  const confirmCurrentReview = async () => {
    if (!currentQueueItem?.externalId) return;
    await run(`review:${currentQueueItem.externalId}`, () => ownerApi.reviewJarvisBacklogItem(currentQueueItem.externalId, { accepted: true }), `${currentQueueItem.externalId} quedó confirmada y SOLUCIONADO.`);
  };

  const submitCurrentReviewNote = async () => {
    const note = reviewNote.trim();
    if (!currentQueueItem?.externalId || !note) {
      setError("Escribí qué falta o qué querés cambiar antes de reactivar la mejora.");
      return;
    }
    await run(`review:${currentQueueItem.externalId}`, () => ownerApi.reviewJarvisBacklogItem(currentQueueItem.externalId, { accepted: false, note }), `${currentQueueItem.externalId} vuelve a ACTIVO con tu devolución, sin crear otra MEJ.`);
  };

  const deleteCurrentReview = async () => {
    if (!currentQueueItem?.externalId) return;
    if (!window.confirm(`¿Eliminar ${currentQueueItem.externalId} de la cola? Esto no la marcará como SOLUCIONADO.`)) return;
    const response = await run(`discard:${currentQueueItem.externalId}`, () => ownerApi.deleteJarvisBacklogItem(currentQueueItem.externalId), (result) => result?.pendingSync
      ? `${currentQueueItem.externalId} salió de la cola local; Google Sheets quedó pendiente de sincronización.`
      : `${currentQueueItem.externalId} fue eliminada de la cola.`);
    if (response?.pendingSync) setError("La decisión quedó guardada localmente, pero Google Sheets todavía no confirmó la eliminación. No se volverá a mostrar como actual/próxima en Jarvis Lab mientras reintenta.");
  };

  const gapPrompt = (gap) => {
    const diagnostic = gap?.lastDiagnostic || gap?.recentOccurrences?.[0]?.diagnostic || {};
    const examples = (gap?.rawExamples || []).map((row) => `- ${row}`).join("\n");
    const candidates = (gap?.candidateActions || []).map((row) => `- ${row.actionKey} — ${row.label} — score ${Number(row.score || 0).toFixed(2)}`).join("\n");
    const files = (diagnostic?.sourceFileHints || []).map((row) => `- ${row}`).join("\n");
    return [
      "WEBPROFIT OWNER — JARVIS FAILURE BRIEF",
      "",
      "HECHOS OBSERVADOS",
      `Frase: ${gap?.rawExamples?.[0] || gap?.utteranceKey || ""}`,
      `Contexto: ${gap?.routeContext || "owner"}`,
      `Ocurrencias: ${gap?.occurrenceCount || 1}`,
      `Motivo: ${gap?.reason || "no_safe_action"}`,
      `Origen: ${gap?.lastInputSource || "unknown"} / ${gap?.lastSource || "unknown"}`,
      diagnostic?.flowFamily ? `Flujo: ${diagnostic.flowFamily}${diagnostic.flowStage ? ` → ${diagnostic.flowStage}` : ""}` : "",
      diagnostic?.flowDescription ? `Qué venía haciendo Jarvis: ${diagnostic.flowDescription}` : "",
      diagnostic?.expectedBehavior ? `Esperado: ${diagnostic.expectedBehavior}` : "",
      diagnostic?.observedBehavior ? `Observado: ${diagnostic.observedBehavior}` : "",
      diagnostic?.failureClass || diagnostic?.failureCode ? `Clase de fallo: ${diagnostic.failureClass || "sin_clasificar"}${diagnostic.failureCode ? ` / ${diagnostic.failureCode}` : ""}` : "",
      diagnostic?.failedActionType || diagnostic?.failedSurface ? `Contrato que falló: ${diagnostic.failedActionType || "acción"}${diagnostic.failedSurface ? ` → ${diagnostic.failedSurface}` : ""}` : "",
      diagnostic?.missingFields?.length ? `Argumentos faltantes: ${diagnostic.missingFields.join(", ")}` : "",
      diagnostic?.activeFormId ? `Formulario: ${diagnostic.activeFormId}${diagnostic.currentFieldName ? ` · campo ${diagnostic.currentFieldName}` : ""}` : "",
      "",
      "Frases reales:",
      examples || `- ${gap?.utteranceKey || ""}`,
      "",
      "Capacidades que el motor consideró:",
      candidates || "- ninguna",
      "",
      "ZONA PROBABLE A REVISAR (NO asumir causa sin abrir fuentes):",
      files || "- resolver/catálogo de capabilities",
      "",
      diagnostic?.stateSnapshot ? `Estado técnico:\n${diagnostic.stateSnapshot}` : "",
      "",
      `Catálogo Owner actual: ${capabilities.length} capacidades registradas.`,
      "",
      "INSTRUCCIÓN PARA EL BOT PROGRAMADOR",
      "Revisá las fuentes reales antes de modificar. Reproducí el flujo y confirmá primero en qué capa falló: interpretación/capability, composición del plan, resolución de entidad o ejecución de action/surface en frontend. Si el backend ya produjo un plan estructurado, no arregles vocabulario ni agregues comandos ad hoc: corregí el contrato o executor que no pudo materializarlo. Hacé el cambio mínimo y agregá una prueba de regresión con esta evidencia.",
    ].filter(Boolean).join("\n");
  };
  const copyGap = async (gap) => {
    try {
      await navigator.clipboard.writeText(gapPrompt(gap));
      setNotice("Gap copiado. Ya lo podés pegar en ChatGPT con contexto real.");
    } catch {
      setError("No pude copiar al portapapeles. Probá desde un navegador con permiso de clipboard.");
    }
  };

  const closeGap = async (gap, status) => {
    setBusy(`gap:${gap.id}`);
    setError("");
    try {
      await ownerApi.updateJarvisCapabilityGap(gap.id, { status });
      setGaps((current) => current.filter((row) => row.id !== gap.id));
    } catch (err) {
      setError(formatApiError(err, "No pude actualizar el gap."));
    } finally {
      setBusy("");
    }
  };

  const loadGapExport = async () => {
    const response = await ownerApi.exportJarvisCapabilityGaps({ status: "open", limit: 500 });
    return response?.export || { markdown: "", total: 0, totalOccurrences: 0 };
  };

  const copyAllGaps = async () => {
    setBusy("gaps-copy-all");
    setError("");
    try {
      const payload = await loadGapExport();
      if (!payload?.markdown) {
        setNotice("No hay frases sin resolver para copiar.");
        return;
      }
      await navigator.clipboard.writeText(payload.markdown);
      setNotice(`Copié ${payload.total || 0} casos agrupados (${payload.totalOccurrences || 0} ocurrencias) listos para pegar en ChatGPT.`);
    } catch (err) {
      setError(formatApiError(err, "No pude preparar el export de Jarvis."));
    } finally {
      setBusy("");
    }
  };

  const exportAllGaps = async () => {
    setBusy("gaps-export");
    setError("");
    try {
      const payload = await loadGapExport();
      if (!payload?.markdown) {
        setNotice("No hay frases sin resolver para exportar.");
        return;
      }
      const blob = new Blob([payload.markdown], { type: "text/markdown;charset=utf-8" });
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const day = new Date().toISOString().slice(0, 10);
      anchor.href = href;
      anchor.download = `jarvis-backlog-programador-${day}.md`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(href);
      setNotice(`Exporté ${payload.total || 0} briefs técnicos (${payload.totalOccurrences || 0} ocurrencias) listos para el bot programador.`);
    } catch (err) {
      setError(formatApiError(err, "No pude exportar las frases sin resolver."));
    } finally {
      setBusy("");
    }
  };

  const unresolvedOccurrences = gaps.reduce((sum, gap) => sum + Number(gap?.occurrenceCount || 1), 0);
  const gapAreas = Object.entries(gaps.reduce((acc, gap) => {
    const diagnostic = gap?.lastDiagnostic || gap?.recentOccurrences?.[0]?.diagnostic || {};
    const area = diagnostic?.technicalArea || "sin_clasificar";
    acc[area] = Number(acc[area] || 0) + Number(gap?.occurrenceCount || 1);
    return acc;
  }, {})).sort((a, b) => b[1] - a[1]);
  const uncoveredRows = (coverage?.rows || []).filter((row) => row.status !== "covered");
  const localPct = clampPercent(metrics?.localPct);
  const aiPct = clampPercent(metrics?.aiPct);
  const gapPct = clampPercent(metrics?.gapPct);
  const fullyCoveredPct = clampPercent(coverage?.fullyCoveredPct);
  const recognizedPct = clampPercent(coverage?.recognizedPct);
  const hasBroken = Number(coverage?.broken || 0) > 0;
  const syncErrorItems = items.filter((item) => item?.sync?.state === "error");
  const syncErrorCount = syncErrorItems.length;
  const retryableSyncErrorCount = syncErrorItems.filter((item) => ["GOOGLE_SHEETS_TIMEOUT", "GOOGLE_SHEETS_RATE_LIMITED", "GOOGLE_SHEETS_UNAVAILABLE"].includes(item?.sync?.lastErrorCode)).length;
  const retryableConfigError = ["GOOGLE_SHEETS_TIMEOUT", "GOOGLE_SHEETS_RATE_LIMITED", "GOOGLE_SHEETS_UNAVAILABLE"].includes(config?.lastErrorCode);
  const transientSyncIssue = retryableConfigError || Boolean(syncErrorCount && retryableSyncErrorCount === syncErrorCount);
  const syncPendingCount = items.filter((item) => item?.sync?.state === "pending").length;
  const sheetHealthy = Boolean(config && !config?.lastError && config?.lastTestedAt && syncErrorCount === 0);

  const focus = hasBroken
    ? { tone: "error", title: "Hay contratos de acciones rotos", body: "Conviene resolverlos antes de ampliar Jarvis: una capability existe pero su ejecución no cierra correctamente." }
    : gaps.length
      ? { tone: "warning", title: `${gaps.length} casos de Jarvis necesitan atención`, body: `${unresolvedOccurrences} ocurrencias quedaron sin una acción segura. Revisá primero las más repetidas.` }
      : Number(coverage?.partial || 0) > 0
        ? { tone: "info", title: "Jarvis está estable; el siguiente límite es la cobertura parcial", body: `${coverage?.partial || 0} acciones se reconocen pero todavía no están cerradas de punta a punta.` }
        : { tone: "success", title: "Jarvis no tiene deuda urgente visible", body: "No hay frases sin resolver ni contratos rotos abiertos en esta lectura." };

  const renderCoverageRows = (limit) => {
    const rows = typeof limit === "number" ? uncoveredRows.slice(0, limit) : uncoveredRows;
    if (!rows.length) return <EmptyState>No hay acciones con cobertura pendiente en esta auditoría.</EmptyState>;
    return (
      <Stack divider={<Divider flexItem />}>
        {rows.map((row) => (
          <Stack key={row.id} direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} sx={{ py: 1.05 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 850 }}>{row.label}</Typography>
              <Typography variant="caption" color="text.secondary">{row.domain} · {row.execution}</Typography>
            </Box>
            <Chip
              size="small"
              color={row.status === "partial" ? "warning" : row.status === "broken" ? "error" : "default"}
              variant="outlined"
              label={row.status === "partial" ? "Parcial" : row.status === "broken" ? "Contrato roto" : "No expuesta"}
            />
            {row.capabilities?.length ? <Typography variant="caption" color="text.secondary" sx={{ maxWidth: { md: 280 } }}>{row.capabilities.join(" · ")}</Typography> : null}
          </Stack>
        ))}
      </Stack>
    );
  };

  const renderGapRows = (limit) => {
    const rows = typeof limit === "number" ? gaps.slice(0, limit) : gaps;
    if (!rows.length) return <EmptyState>No hay frases sin resolver abiertas.</EmptyState>;
    return (
      <Stack spacing={1}>
        {rows.map((gap) => (
          <Paper key={gap.id} variant="outlined" sx={{ p: 1.35, borderRadius: 2.7 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} alignItems={{ md: "center" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={.55} flexWrap="wrap" useFlexGap sx={{ mb: .65 }}>
                  <Chip size="small" color="warning" label={`${gap.occurrenceCount || 1}×`} />
                  <Chip size="small" variant="outlined" label={gap.routeContext || "owner"} />
                  <Chip size="small" variant="outlined" label={gap.lastInputSource === "voice" ? "voz" : gap.lastInputSource === "text" ? "texto" : gap.lastInputSource || "origen desconocido"} />
                  <Chip size="small" variant="outlined" label={gap.reason || "sin acción"} />
                </Stack>
                <Typography sx={{ fontWeight: 900 }}>{gap.rawExamples?.[0] || gap.utteranceKey}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: .35 }}>
                  Última vez {dateLabel(gap.lastSeenAt)}{gap.lastCurrentPath ? ` · ${gap.lastCurrentPath}` : ""}
                </Typography>
                {(() => {
                  const diagnostic = gap?.lastDiagnostic || gap?.recentOccurrences?.[0]?.diagnostic || null;
                  return diagnostic ? (
                    <Box sx={{ mt: .65, p: .9, borderRadius: 2, bgcolor: "action.hover" }}>
                      <Typography variant="caption" sx={{ display: "block", fontWeight: 900 }}>
                        {diagnostic.flowFamily || "flujo"}{diagnostic.flowStage ? ` → ${diagnostic.flowStage}` : ""}{diagnostic.technicalArea ? ` · ${diagnostic.technicalArea}` : ""}
                      </Typography>
                      {diagnostic.flowDescription ? <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: .2 }}>Venía haciendo: {diagnostic.flowDescription}</Typography> : null}
                      {diagnostic.expectedBehavior ? <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: .2 }}><strong>Esperado:</strong> {diagnostic.expectedBehavior}</Typography> : null}
                      {diagnostic.observedBehavior ? <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: .2 }}><strong>Observado:</strong> {diagnostic.observedBehavior}</Typography> : null}
                      {diagnostic.failureClass || diagnostic.failureCode ? (
                        <Stack direction="row" spacing={.5} flexWrap="wrap" useFlexGap sx={{ mt: .55 }}>
                          <Chip size="small" color="error" variant="outlined" label={`${diagnostic.failureClass || "fallo de ejecución"}${diagnostic.failureCode ? ` · ${diagnostic.failureCode}` : ""}`} />
                          {diagnostic.failedActionType || diagnostic.failedSurface ? <Chip size="small" variant="outlined" label={`${diagnostic.failedActionType || "acción"}${diagnostic.failedSurface ? ` → ${diagnostic.failedSurface}` : ""}`} /> : null}
                          {diagnostic.missingFields?.length ? <Chip size="small" variant="outlined" label={`Falta: ${diagnostic.missingFields.join(", ")}`} /> : null}
                        </Stack>
                      ) : null}
                    </Box>
                  ) : null;
                })()}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: .45 }}>
                  {(() => {
                    const diagnostic = gap?.lastDiagnostic || gap?.recentOccurrences?.[0]?.diagnostic || {};
                    if (diagnostic.failureClass || diagnostic.failureCode) return "La intención ya llegó a un plan backend; este caso corresponde a materialización/ejecución del contrato, no a falta de vocabulario.";
                    return gap.candidateActions?.length
                      ? `Cerca de: ${gap.candidateActions.slice(0, 3).map((row) => `${row.label} ${Math.round(Number(row.score || 0) * 100)}%`).join(" · ")}`
                      : "Ninguna capacidad actual quedó cerca con seguridad.";
                  })()}
                </Typography>
              </Box>
              <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="outlined" onClick={() => copyGap(gap)}>Copiar brief técnico</Button>
                <Button size="small" onClick={() => closeGap(gap, "covered")} disabled={busy === `gap:${gap.id}`}>Ya cubierto</Button>
                <Button size="small" color="inherit" onClick={() => closeGap(gap, "dismissed")} disabled={busy === `gap:${gap.id}`}>Ignorar</Button>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  };

  return (
    <Stack data-owner-jarvis-section="jarvis-lab" spacing={1.5} sx={{ width: "100%", pb: 5 }}>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          borderColor: "primary.main",
          background: "linear-gradient(135deg, rgba(255,122,0,.11) 0%, rgba(255,255,255,.98) 48%, rgba(255,255,255,1) 100%)",
        }}
      >
        <Box sx={{ p: { xs: 1.6, md: 2.2 } }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} justifyContent="space-between">
            <Box>
              <Stack direction="row" spacing={.9} alignItems="center">
                <Box sx={{ width: 38, height: 38, borderRadius: 2.4, bgcolor: "primary.main", color: "primary.contrastText", display: "grid", placeItems: "center" }}>
                  <BugReportRoundedIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 950, lineHeight: 1.05 }}>Jarvis Lab</Typography>
                  <Typography variant="caption" color="text.secondary">Centro de control · aprendizaje · cobertura · backlog</Typography>
                </Box>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
                Acá ves qué entiende Jarvis, dónde falla, cuánto resuelve localmente y qué deuda queda antes de seguir ampliándolo.
              </Typography>
            </Box>
            <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap alignItems="center">
              <Chip size="small" color={hasBroken ? "error" : "success"} icon={hasBroken ? <WarningAmberRoundedIcon /> : <CheckCircleRoundedIcon />} label={hasBroken ? `${coverage?.broken || 0} contratos rotos` : "Contratos sanos"} />
              <Chip size="small" color={gaps.length ? "warning" : "success"} variant="outlined" label={`${gaps.length} sin resolver`} />
              <Chip size="small" color={sheetHealthy ? "success" : config ? "warning" : "default"} variant="outlined" label={sheetHealthy ? "Sheets conectado" : config ? "Sheets por revisar" : "Sheets opcional"} />
              <Button size="small" variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading || Boolean(busy)}>Actualizar</Button>
            </Stack>
          </Stack>
        </Box>
        {loading ? <LinearProgress /> : null}
      </Paper>

      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      {notice ? <Alert severity="success" onClose={() => setNotice("")}>{notice}</Alert> : null}

      <Paper variant="outlined" sx={{ borderRadius: 3, px: .6 }}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Secciones de Jarvis Lab"
          sx={{ minHeight: 48, "& .MuiTab-root": { minHeight: 48, textTransform: "none", fontWeight: 850 } }}
        >
          {TAB_META.map((item) => <Tab key={item.value} value={item.value} icon={item.icon} iconPosition="start" label={item.label} />)}
        </Tabs>
      </Paper>

      {tab === "overview" ? (
        <Stack spacing={1.5}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1 }}>
            <MetricCard label="RESUELTO LOCAL" value={`${localPct}%`} detail={`${metrics?.local || 0} turnos`} tone="success" icon={<AutoAwesomeRoundedIcon />} />
            <MetricCard label="USÓ GPT" value={`${aiPct}%`} detail={`${metrics?.ai || 0} turnos`} tone="info" icon={<AutoAwesomeMotionRoundedIcon />} />
            <MetricCard label="SIN RESOLVER" value={`${gapPct}%`} detail={`${metrics?.gap || 0} turnos`} tone={Number(metrics?.gap || 0) ? "warning" : "success"} icon={<WarningAmberRoundedIcon />} />
            <MetricCard label="COBERTURA COMPLETA" value={`${fullyCoveredPct}%`} detail={`${coverage?.covered || 0}/${coverage?.totalActions || 0} acciones`} tone={hasBroken ? "error" : "default"} icon={<HubRoundedIcon />} />
          </Box>

          <Alert severity={focus.tone} icon={focus.tone === "success" ? <CheckCircleRoundedIcon /> : <WarningAmberRoundedIcon />}>
            <Typography sx={{ fontWeight: 900 }}>{focus.title}</Typography>
            <Typography variant="body2">{focus.body}</Typography>
          </Alert>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1.5 }}>
            <SectionCard title="Cómo está resolviendo" description="Últimos 7 días. La intención ideal es que la mayoría sea local y GPT quede para ambigüedad o extracción compleja.">
              <Stack spacing={1.4}>
                <Box>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 800 }}>Local</Typography><Typography variant="body2">{localPct}%</Typography></Stack>
                  <LinearProgress variant="determinate" value={localPct} color="success" sx={{ height: 8, borderRadius: 10, mt: .5 }} />
                </Box>
                <Box>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 800 }}>GPT</Typography><Typography variant="body2">{aiPct}%</Typography></Stack>
                  <LinearProgress variant="determinate" value={aiPct} color="info" sx={{ height: 8, borderRadius: 10, mt: .5 }} />
                </Box>
                <Box>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 800 }}>Gap</Typography><Typography variant="body2">{gapPct}%</Typography></Stack>
                  <LinearProgress variant="determinate" value={gapPct} color={gapPct ? "warning" : "success"} sx={{ height: 8, borderRadius: 10, mt: .5 }} />
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850 }}>ACCIONES MÁS USADAS</Typography>
                  {metrics?.topActions?.length ? (
                    <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap sx={{ mt: .7 }}>
                      {metrics.topActions.slice(0, 7).map((row) => <Chip key={`${row.actionKey}:${row.mode}`} size="small" variant="outlined" label={`${row.actionKey} · ${row.count}`} />)}
                    </Stack>
                  ) : <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>Todavía no hay muestra suficiente.</Typography>}
                </Box>
              </Stack>
            </SectionCard>

            <SectionCard title="Cobertura del Owner" description={`${recognizedPct}% de las acciones manuales ya son reconocibles completa o parcialmente.`}>
              <Stack spacing={1.3}>
                <Box>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 850 }}>Cobertura reconocible</Typography><Typography variant="body2">{recognizedPct}%</Typography></Stack>
                  <LinearProgress variant="determinate" value={recognizedPct} sx={{ height: 8, borderRadius: 10, mt: .5 }} />
                </Box>
                <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap>
                  <Chip size="small" color="success" label={`${coverage?.covered || 0} cubiertas`} />
                  <Chip size="small" color="warning" variant="outlined" label={`${coverage?.partial || 0} parciales`} />
                  <Chip size="small" variant="outlined" label={`${coverage?.notExposed || 0} no expuestas`} />
                  <Chip size="small" color={hasBroken ? "error" : "success"} variant="outlined" label={`${coverage?.broken || 0} rotas`} />
                </Stack>
                <Divider />
                {renderCoverageRows(4)}
                {uncoveredRows.length > 4 ? <Button size="small" onClick={() => setTab("learning")}>Ver toda la cobertura</Button> : null}
              </Stack>
            </SectionCard>
          </Box>

          <SectionCard
            title="Lo que Jarvis no entendió"
            description="Los casos repetidos pesan más. No depende de Google Sheets: esto vive en Mongo y representa uso real."
            action={gaps.length ? <Button size="small" onClick={() => setTab("learning")}>Abrir aprendizaje</Button> : null}
          >
            {renderGapRows(4)}
          </SectionCard>
        </Stack>
      ) : null}

      {tab === "learning" ? (
        <Stack spacing={1.5}>
          <SectionCard
            title="Frases sin resolver"
            description={`Hay ${gaps.length} casos agrupados y ${unresolvedOccurrences} ocurrencias abiertas. Cada caso conserva flujo, etapa, esperado vs observado y zona probable para que el bot programador pueda reproducirlo.`}
            action={(
              <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="outlined" startIcon={<ContentCopyRoundedIcon />} onClick={copyAllGaps} disabled={!gaps.length || Boolean(busy)}>Copiar todos</Button>
                <Button size="small" variant="contained" startIcon={<DownloadRoundedIcon />} onClick={exportAllGaps} disabled={!gaps.length || Boolean(busy)}>Exportar para programador</Button>
              </Stack>
            )}
          >
            {gapAreas.length ? (
              <Stack direction="row" spacing={.55} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
                {gapAreas.slice(0, 8).map(([area, count]) => <Chip key={area} size="small" variant="outlined" label={`${area} · ${count}`} />)}
              </Stack>
            ) : null}
            {renderGapRows()}
          </SectionCard>

          <SectionCard title="Cobertura detallada" description="Acciones manuales del Owner que Jarvis reconoce sólo parcialmente o todavía no expone de forma segura.">
            <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap sx={{ mb: 1.3 }}>
              <Chip size="small" color="success" label={`${coverage?.covered || 0} cubiertas`} />
              <Chip size="small" color="warning" variant="outlined" label={`${coverage?.partial || 0} parciales`} />
              <Chip size="small" variant="outlined" label={`${coverage?.notExposed || 0} no expuestas`} />
              <Chip size="small" variant="outlined" label={`${coverage?.totalActions || 0} auditadas`} />
            </Stack>
            {renderCoverageRows()}
          </SectionCard>
        </Stack>
      ) : null}

      {tab === "backlog" ? (
        <Stack spacing={1.5}>
          <SectionCard
            title="Cola operativa del Product Team"
            description="Lee el estado real del backlog sincronizado. Trabajo actual y próxima fila no se deducen por fecha de actualización."
            action={queue?.sync?.lastSyncedAt ? <Chip size="small" variant="outlined" label={`Sync ${dateLabel(queue.sync.lastSyncedAt)}`} /> : null}
          >
            {loading ? <Box sx={{ py: 3, textAlign: "center" }}><CircularProgress size={24} /></Box> : !queue ? (
              <Alert severity="warning">No pude calcular la cola operativa. El backlog histórico sigue disponible abajo.</Alert>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.35fr) minmax(280px, .65fr)" }, gap: 1.2 }}>
                <Paper variant="outlined" sx={{ p: { xs: 1.2, md: 1.5 }, borderRadius: 3, minWidth: 0 }}>
                  <Stack direction="row" spacing={.7} justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900, letterSpacing: .3 }}>TRABAJO ACTUAL</Typography>
                      <Typography sx={{ fontWeight: 950, mt: .2 }}>
                        {queue.current?.mode === "working" ? "Trabajando ahora" : queue.current?.mode === "review" ? "Esperando tu revisión" : queue.current?.mode === "inconsistent" ? "Inconsistencia operativa" : "Sin trabajo activo"}
                      </Typography>
                    </Box>
                    {queue.current?.mode === "working" ? <Chip size="small" color="info" label="TRABAJANDO" /> : null}
                    {queue.current?.mode === "review" ? <Chip size="small" color="warning" label="HECHO_POR_BOT" /> : null}
                    {queue.current?.mode === "inconsistent" ? <Chip size="small" color="error" label={`${queue.current?.items?.length || 0} activas`} /> : null}
                  </Stack>

                  {queue.current?.mode === "inconsistent" ? (
                    <Stack spacing={.8}>
                      <Alert severity="error">Hay más de una fila en TRABAJANDO. Jarvis Lab no elige una arbitrariamente.</Alert>
                      {(queue.current?.items || []).map((item) => (
                        <Paper key={backlogItemId(item)} variant="outlined" sx={{ p: 1, borderRadius: 2.2 }}>
                          <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap>
                            <Chip size="small" label={backlogItemId(item)} />
                            <Chip size="small" color="info" label="TRABAJANDO" />
                            {item.sheetRowNumber ? <Chip size="small" variant="outlined" label={`Fila ${item.sheetRowNumber}`} /> : null}
                          </Stack>
                          <Typography sx={{ fontWeight: 850, mt: .6 }}>{item.title}</Typography>
                        </Paper>
                      ))}
                    </Stack>
                  ) : queue.current?.item ? (
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={.55} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={backlogItemId(queue.current.item)} />
                        <Chip size="small" color={statusTone(queue.current.item.status)} variant="outlined" label={queue.current.item.status} />
                        <Chip size="small" variant="outlined" label={queue.current.item.priority || "MEDIA"} />
                        {queue.current.item.sheetRowNumber ? <Chip size="small" variant="outlined" label={`Fila ${queue.current.item.sheetRowNumber}`} /> : <Chip size="small" variant="outlined" label="Sin posición confirmada" />}
                        <Chip size="small" variant="outlined" label={`${Number(queue.current.item.attempts || 0)} intento(s)`} />
                      </Stack>
                      <Box>
                        <Typography sx={{ fontWeight: 950 }}>{queue.current.item.title}</Typography>
                        <Typography variant="caption" color="text.secondary">Última ejecución: {dateLabel(queue.current.item.lastExecutionAt || queue.current.item.updatedAt)}</Typography>
                        {(queue.current.item?.captureContext?.breadcrumb || queue.current.item?.autoBuild?.routeTree?.breadcrumb) ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Contexto: {queue.current.item?.captureContext?.breadcrumb || queue.current.item?.autoBuild?.routeTree?.breadcrumb}</Typography>
                        ) : null}
                      </Box>

                      {queue.current?.mode === "review" ? (
                        <Stack spacing={1}>
                          <Box sx={{ p: 1.1, borderRadius: 2.2, bgcolor: "action.hover" }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>RESULTADO DEL BOT</Typography>
                            <Typography
                              variant="body2"
                              sx={{ mt: .55, whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxHeight: resultExpanded ? "none" : 180, overflow: "hidden" }}
                            >
                              {queue.current.item.botResult || "El bot no dejó RESULTADO_BOT."}
                            </Typography>
                            {String(queue.current.item.botResult || "").length > 700 ? (
                              <Button size="small" onClick={() => setResultExpanded((value) => !value)} sx={{ mt: .5, px: 0 }}>{resultExpanded ? "Ver menos" : "Leer resultado completo"}</Button>
                            ) : null}
                          </Box>
                          {queue.current.item.reviewGuide ? <Alert severity="info" icon={false}><b>Guía de revisión:</b> {queue.current.item.reviewGuide}</Alert> : null}
                          {queue.current.item.humanNote ? (
                            <Box sx={{ maxHeight: 120, overflow: "auto", px: .2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}><b>Nota humana:</b> {queue.current.item.humanNote}</Typography>
                            </Box>
                          ) : null}
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={.8}>
                            <Button variant="contained" color="success" disabled={Boolean(busy)} onClick={confirmCurrentReview}>Confirmar</Button>
                            <Button variant="outlined" disabled={Boolean(busy)} onClick={() => setReviewEditing((value) => !value)}>Seguir editando</Button>
                            <Button variant="outlined" color="error" disabled={Boolean(busy)} onClick={deleteCurrentReview}>Eliminar</Button>
                          </Stack>
                          {reviewEditing ? (
                            <Stack spacing={.7}>
                              <TextField
                                multiline
                                minRows={2}
                                value={reviewNote}
                                onChange={(event) => setReviewNote(event.target.value)}
                                placeholder="Qué falta, qué querés corregir o qué información agregar a esta misma MEJ"
                                fullWidth
                              />
                              <Box><Button variant="contained" disabled={Boolean(busy) || !reviewNote.trim()} onClick={submitCurrentReviewNote}>Guardar devolución en la misma MEJ</Button></Box>
                            </Stack>
                          ) : null}
                        </Stack>
                      ) : null}

                      {queue.current?.mode === "working" && queue.pendingReviewCount ? (
                        <Alert severity="warning">Hay {queue.pendingReviewCount} mejora(s) HECHO_POR_BOT esperando decisión del Owner.</Alert>
                      ) : null}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No hay filas TRABAJANDO ni HECHO_POR_BOT pendientes de revisión.</Typography>
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: { xs: 1.2, md: 1.5 }, borderRadius: 3, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900, letterSpacing: .3 }}>PRÓXIMA</Typography>
                  {queue.next ? (
                    <Stack spacing={.8} sx={{ mt: .7 }}>
                      <Stack direction="row" spacing={.5} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={backlogItemId(queue.next)} />
                        <Chip size="small" variant="outlined" label={queue.next.priority || "MEDIA"} />
                        <Chip size="small" variant="outlined" label={`Fila ${queue.next.sheetRowNumber}`} />
                      </Stack>
                      <Typography sx={{ fontWeight: 900 }}>{queue.next.title}</Typography>
                      {queue.current?.mode === "review" || queue.pendingReviewCount ? <Alert severity="warning" icon={false}>Hay una decisión humana pendiente; esta fila es la siguiente en orden, no significa que ya esté ejecutándose.</Alert> : null}
                      <Typography variant="caption" color="text.secondary">Orden confirmado por sheetRowNumber sincronizado; prioridad no reordena la cola.</Typography>
                    </Stack>
                  ) : (
                    <Stack spacing={.7} sx={{ mt: .7 }}>
                      <Typography variant="body2" color="text.secondary">{queue.nextReason || "No hay próxima fila confirmada."}</Typography>
                      {queue.unpositionedActiveCount ? <Chip size="small" variant="outlined" label={`${queue.unpositionedActiveCount} ACTIVO sin posición confirmada`} sx={{ alignSelf: "flex-start" }} /> : null}
                    </Stack>
                  )}
                  {queue.sync?.error ? <Alert severity="info" sx={{ mt: 1 }}>{queue.sync.error}</Alert> : null}
                </Paper>
              </Box>
            )}
          </SectionCard>

          <SectionCard
            title="Backlog reciente"
            description="Copia local de mejoras y errores. Si Sheets está caído o desconectado, los reportes no se pierden."
            action={(
              <Stack direction="row" spacing={.5} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${items.length} locales`} />
                {syncPendingCount ? <Chip size="small" color="warning" variant="outlined" label={`${syncPendingCount} por enviar`} /> : null}
                {syncErrorCount ? <Chip size="small" color="error" label={`${syncErrorCount} con error`} /> : null}
              </Stack>
            )}
          >
            {loading ? <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={24} /></Box> : items.length ? (
              <Stack spacing={.8}>
                {items.slice(0, 80).map((item) => (
                  <Paper key={item._id || item.externalId} variant="outlined" sx={{ p: 1.2, borderRadius: 2.5 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={.8} alignItems={{ sm: "center" }}>
                      <Stack direction="row" spacing={.5} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={item.externalId || item.localRef || `LOCAL-${String(item._id || "").slice(-6).toUpperCase()}`} />
                        <Chip size="small" color={statusTone(item.status)} variant={item.status === "ACTIVO" ? "outlined" : "filled"} label={item.status || "ACTIVO"} />
                        <Chip size="small" variant="outlined" label={item.priority || "MEDIA"} />
                        {item?.autoBuild?.enabled ? <Chip size="small" color="secondary" variant="outlined" label="auto-backlog" /> : null}
                        {item?.autoBuild?.enabled ? <Chip size="small" variant="outlined" label={`${item.autoBuild.occurrenceCount || 1} ocurrencias · ${item.autoBuild.episodeCount || 1} episodios`} /> : null}
                        {item?.sync?.state === "pending" ? <Chip size="small" color="warning" variant="outlined" label="por sincronizar" /> : null}
                        {item?.sync?.state === "error" ? <Chip size="small" color={["GOOGLE_SHEETS_TIMEOUT", "GOOGLE_SHEETS_RATE_LIMITED", "GOOGLE_SHEETS_UNAVAILABLE"].includes(item?.sync?.lastErrorCode) ? "warning" : "error"} variant="outlined" label={["GOOGLE_SHEETS_TIMEOUT", "GOOGLE_SHEETS_RATE_LIMITED", "GOOGLE_SHEETS_UNAVAILABLE"].includes(item?.sync?.lastErrorCode) ? "sync pendiente de reintento" : "error de sync"} /> : null}
                      </Stack>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 850 }}>{item.title}</Typography>
                        {item?.affectedContext?.sectionLabel ? (
                          <Typography variant="caption" sx={{ display: "block", mt: .2, fontWeight: 800 }}>
                            Afecta: {[item.affectedContext.sectionLabel, item.affectedContext.entityLabel].filter(Boolean).join(" · ")}
                          </Typography>
                        ) : null}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          Reportado desde: {item?.autoBuild?.routeTree?.breadcrumb || item?.selfBuildEvidence?.routeBreadcrumb || item?.captureContext?.breadcrumb || [item?.sourceContext?.sectionLabel, item?.sourceContext?.entityLabel].filter(Boolean).join(" · ") || "Sin ruta funcional"}
                        </Typography>
                        {(item?.autoBuild?.routeTree?.uiPath || item?.sourceContext?.route) ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            Ruta UI: {item?.autoBuild?.routeTree?.uiPath || item?.sourceContext?.route}
                          </Typography>
                        ) : null}
                        {item?.autoBuild?.enabled && item?.selfBuildEvidence?.triggeringUtterance ? (
                          <Typography variant="caption" sx={{ display: "block", mt: .35, fontWeight: 700 }}>
                            Último fallo: “{item.selfBuildEvidence.triggeringUtterance}” · {item.selfBuildEvidence.messageCount || 0} mensajes preservados
                          </Typography>
                        ) : null}
                        {item?.autoBuild?.enabled && item?.selfBuildEvidence?.conversationPreview?.length ? (
                          <Box sx={{ mt: .5, pl: 1, borderLeft: "2px solid", borderColor: "divider" }}>
                            {item.selfBuildEvidence.conversationPreview.slice(-4).map((message, messageIndex) => (
                              <Typography key={`${item._id}-preview-${messageIndex}`} variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                <b>{message.role === "assistant" ? "Jarvis" : "Vos"}:</b> {message.text}
                              </Typography>
                            ))}
                          </Box>
                        ) : null}
                        {!item?.autoBuild?.enabled && item?.captureContext?.recentMessages?.length ? (
                          <Box sx={{ mt: .5, pl: 1, borderLeft: "2px solid", borderColor: "divider" }}>
                            {item.captureContext.recentMessages.filter((message) => message.role !== "system").slice(-4).map((message, messageIndex) => (
                              <Typography key={`${item._id}-capture-${messageIndex}`} variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                <b>{message.role === "assistant" ? "Jarvis" : "Vos"}:</b> {message.text}
                              </Typography>
                            ))}
                          </Box>
                        ) : null}
                        {item?.sync?.state === "error" && item?.sync?.lastError ? (
                          <Typography variant="caption" color={["GOOGLE_SHEETS_TIMEOUT", "GOOGLE_SHEETS_RATE_LIMITED", "GOOGLE_SHEETS_UNAVAILABLE"].includes(item?.sync?.lastErrorCode) ? "warning.main" : "error.main"} sx={{ display: "block", mt: .25 }}>
                            {["GOOGLE_SHEETS_TIMEOUT", "GOOGLE_SHEETS_RATE_LIMITED", "GOOGLE_SHEETS_UNAVAILABLE"].includes(item?.sync?.lastErrorCode) ? "Google no respondió a tiempo. La fila está segura en Mongo y queda pendiente para el próximo sync." : item.sync.lastError}
                          </Typography>
                        ) : null}
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : <EmptyState>Todavía no hay fixes capturados.</EmptyState>}
          </SectionCard>

          <SectionCard title="Contrato de Google Sheet" description="Jarvis valida estas 11 columnas antes de escribir. Si el formato no coincide, frena sin modificar la hoja.">
            <Stack direction="row" spacing={.55} flexWrap="wrap" useFlexGap>
              {HEADERS.map((header) => <Chip key={header} size="small" variant="outlined" label={header} />)}
            </Stack>
            <Divider sx={{ my: 1.4 }} />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1 }}>
              <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2.5 }}><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850 }}>DICTADO</Typography><Typography variant="body2" sx={{ mt: .3 }}>“Jarvis, anotá un error: en cobros no aparece la próxima fecha”.</Typography></Paper>
              <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2.5 }}><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850 }}>SEGUIMIENTO</Typography><Typography variant="body2" sx={{ mt: .3 }}>“MEJ-126 sigue mal porque el monto se duplica”.</Typography></Paper>
              <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2.5 }}><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850 }}>CICLO</Typography><Typography variant="body2" sx={{ mt: .3 }}>HECHO_POR_BOT → revisión humana → SOLUCIONADO o vuelve a ACTIVO.</Typography></Paper>
            </Box>
          </SectionCard>
        </Stack>
      ) : null}

      {tab === "connection" ? (
        <Stack spacing={1.5}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, .8fr) minmax(0, 1.2fr)" }, gap: 1.5 }}>
            <SectionCard title="Estado de conexión" description="Google Sheets es opcional: Mongo sigue guardando aprendizaje y backlog aunque esta integración esté apagada.">
              <Stack spacing={1.1}>
                <Stack direction="row" spacing={.8} alignItems="center">
                  {sheetHealthy ? <CheckCircleRoundedIcon color="success" /> : <WarningAmberRoundedIcon color={config ? "warning" : "disabled"} />}
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>{sheetHealthy ? "Conectado y sincronizando bien" : syncErrorCount ? (retryableSyncErrorCount === syncErrorCount ? "Conectado, con filas esperando reintento" : "Conectado, con filas que requieren revisión") : retryableConfigError ? "Conectado, Google está respondiendo lento" : config?.lastError ? "Conexión/sync con error" : config ? "Configurado, falta validar" : "Sin conectar"}</Typography>
                    <Typography variant="caption" color="text.secondary">Última prueba: {dateLabel(config?.lastTestedAt)}</Typography>
                  </Box>
                </Stack>
                <Divider />
                <Stack spacing={.75}>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Configuración guardada</Typography><Chip size="small" color={config ? "success" : "default"} label={config ? "Sí" : "No"} /></Stack>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Sincronización</Typography><Chip size="small" color={config?.enabled ? "success" : "default"} variant="outlined" label={config?.enabled ? "Activa" : "Pausada"} /></Stack>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Última sync completa</Typography><Typography variant="body2" color="text.secondary">{dateLabel(config?.lastSyncedAt)}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Último intento</Typography><Typography variant="body2" color="text.secondary">{dateLabel(config?.lastSyncAttemptAt)}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Cola local</Typography><Stack direction="row" spacing={.5}><Chip size="small" variant="outlined" color={syncPendingCount ? "warning" : "default"} label={`${syncPendingCount} pendientes`} />{syncErrorCount ? <Chip size="small" color={retryableSyncErrorCount === syncErrorCount ? "warning" : "error"} label={retryableSyncErrorCount === syncErrorCount ? `${syncErrorCount} para reintentar` : `${syncErrorCount} con error`} /> : null}</Stack></Stack>
                </Stack>
                {config?.lastError ? <Alert severity={transientSyncIssue ? "info" : "warning"}>{transientSyncIssue ? "Google respondió lento o no estuvo disponible. El backlog ya está guardado en Mongo y cualquier fila afectada queda lista para reintentar en el próximo sync; podés forzarlo ahora." : config.lastError}</Alert> : null}
              </Stack>
            </SectionCard>

            <SectionCard title="Cómo conectarlo" description="Tres pasos. La credencial queda en backend; Jarvis no necesita tu contraseña personal de Google.">
              <Stack spacing={1}>
                {["Creá una Service Account y descargá su JSON.", "Compartí la planilla con client_email como Editor.", "Pegá el JSON completo abajo, guardá y tocá Probar."].map((text, index) => (
                  <Stack key={text} direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "primary.main", color: "primary.contrastText", display: "grid", placeItems: "center", fontSize: 13, fontWeight: 950, flex: "0 0 auto" }}>{index + 1}</Box>
                    <Typography variant="body2">{text}</Typography>
                  </Stack>
                ))}
              </Stack>
            </SectionCard>
          </Box>

          <SectionCard
            title="Google Sheets"
            description="Configuración técnica. Si pegás el JSON completo de la Service Account, Jarvis toma automáticamente private_key y client_email."
            action={config ? <Chip size="small" color={config?.enabled ? "success" : "default"} variant="outlined" label={config?.enabled ? "Sync activa" : "Sync pausada"} /> : null}
          >
            <Stack spacing={1.25}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 220px" }, gap: 1 }}>
                <TextField fullWidth size="small" label="URL o ID de la planilla" value={form.spreadsheetId} onChange={(e) => patch("spreadsheetId", e.target.value)} />
                <TextField fullWidth size="small" label="Pestaña" value={form.sheetName} onChange={(e) => patch("sheetName", e.target.value)} />
              </Box>
              <TextField fullWidth size="small" label="Email de Service Account" value={form.serviceAccountEmail} onChange={(e) => patch("serviceAccountEmail", e.target.value)} placeholder="jarvis@proyecto.iam.gserviceaccount.com" />
              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={9}
                size="small"
                label={config?.hasPrivateKey ? "JSON / private key · dejar vacío para conservar la actual" : "JSON completo de Service Account o private_key"}
                value={form.privateKey}
                onChange={(e) => patch("privateKey", e.target.value)}
                placeholder={'{ "type": "service_account", "private_key": "-----BEGIN PRIVATE KEY-----..." }'}
                helperText="Acepta JSON completo, PEM normal o PEM con \\n escapados. Nunca pegues una key de OpenAI en este campo."
              />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "150px 180px 1fr" }, gap: 1, alignItems: "center" }}>
                <TextField fullWidth size="small" label="Prefijo ID" value={form.idPrefix} onChange={(e) => patch("idPrefix", e.target.value)} />
                <TextField fullWidth size="small" label="Origen" value={form.origin} onChange={(e) => patch("origin", e.target.value)} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <FormControlLabel control={<Switch checked={Boolean(form.enabled)} onChange={(e) => patch("enabled", e.target.checked)} />} label="Sincronizar" />
                  <FormControlLabel control={<Switch checked={Boolean(form.reviewEnabled)} onChange={(e) => patch("reviewEnabled", e.target.checked)} />} label="Preguntar revisión" />
                </Stack>
              </Box>
              <Divider />
              <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap alignItems="center">
                <Button variant="contained" onClick={save} disabled={Boolean(busy)}>Guardar</Button>
                <Button startIcon={<ScienceRoundedIcon />} onClick={test} disabled={Boolean(busy) || !config}>Probar</Button>
                <Button startIcon={<DescriptionIcon />} onClick={prepare} disabled={Boolean(busy) || !config}>Preparar hoja</Button>
                <Button startIcon={<CloudSyncRoundedIcon />} onClick={sync} disabled={Boolean(busy) || !config}>{syncErrorCount || retryableConfigError ? "Reintentar ahora" : "Sincronizar"}</Button>
                {config ? <Button color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={disconnect} disabled={Boolean(busy)}>Desconectar</Button> : null}
                {busy ? <CircularProgress size={20} /> : null}
              </Stack>
            </Stack>
          </SectionCard>
        </Stack>
      ) : null}
    </Stack>
  );
}
